import React, {useEffect, useRef, useState} from 'react';
import styled, {css} from 'styled-components';
import {useIntl} from 'react-intl';
import {FlexRow, tokensForThemeProvider} from '@forecast-it/design-system';
import {D} from './design-tokens';
import type {StatusStep} from './useAIChatStream';
import {CheckIcon, SpinnerIcon, IdleIcon} from './icons';

const {font} = tokensForThemeProvider.sky.sys;

const bodyFont = css`
	${font.body['1']}
`;

const detailFont = css`
	${font.detail['1']}
`;

const FADE_DELAY_MS = 1500;
const FADE_DURATION_MS = 1600;

// ── Tool label translation keys ───────────────────────────────────────────────

const TOOL_KEY_MAP: Record<string, string> = {
	list_projects: 'forecast_agent.step.tool.list_projects',
	get_project_status: 'forecast_agent.step.tool.get_project_status',
	get_portfolio_health: 'forecast_agent.step.tool.get_portfolio_health',
	get_portfolio_summary: 'forecast_agent.step.tool.get_portfolio_summary',
	get_team_utilisation: 'forecast_agent.step.tool.get_team_utilisation',
	get_resource_availability: 'forecast_agent.step.tool.get_resource_availability',
	get_time_entries: 'forecast_agent.step.tool.get_time_entries',
	get_current_datetime: 'forecast_agent.step.tool.get_current_datetime',
	search_projects: 'forecast_agent.step.tool.search_projects',
	get_allocations: 'forecast_agent.step.tool.get_allocations',
	get_person_utilisation: 'forecast_agent.step.tool.get_person_utilisation',
	get_team_availability: 'forecast_agent.step.tool.get_team_availability',
	get_non_billable_time: 'forecast_agent.step.tool.get_non_billable_time',
	get_time_analysis: 'forecast_agent.step.tool.get_time_analysis',
	compare_time_periods: 'forecast_agent.step.tool.compare_time_periods',
	get_project_billing_breakdown: 'forecast_agent.step.tool.get_project_billing_breakdown',
};

// ── Styled components ─────────────────────────────────────────────────────────

const StepsWrapper = styled.div`
	margin-top: 6px;
`;

const StepsRow = styled(FlexRow)`
	flex-wrap: wrap;
	gap: 6px;
`;

const StepChip = styled.span<{state: 'done' | 'active' | 'idle'; size: 'large' | 'small'; fading: boolean}>`
	display: inline-flex;
	align-items: center;
	border-radius: 16px;
	background: ${D.bg};
	color: ${D.v2.textPrimary};
	animation: ai-fade 0.2s ease-out;
	border: 2px solid ${({state}) => (state === 'done' ? D.v2.statusGreen : state === 'active' ? D.v2.accent : D.inputBorder)};
	opacity: ${({fading}) => (fading ? 0 : 1)};
	transition: ${({fading}) => (fading ? `opacity ${FADE_DURATION_MS}ms ease` : 'none')};

	${({size}) =>
		size === 'large'
			? css`
					${bodyFont}
					gap: 12px;
					padding: 8px 16px;
			  `
			: css`
					${detailFont}
					gap: 8px;
					padding: 8px;
			  `}
`;

const ChipDot = styled.span<{state: 'done' | 'active' | 'idle'}>`
	display: inline-block;
	width: 8px;
	height: 8px;
	border-radius: 50%;
	flex-shrink: 0;
	background: ${({state}) => (state === 'done' ? D.v2.statusGreen : state === 'active' ? D.v2.accent : D.inputBorder)};
`;

const ChipInner = styled.span`
	display: inline-flex;
	align-items: center;
	gap: inherit;
	padding: 0 8px;
`;

// ── Component ─────────────────────────────────────────────────────────────────

type StatusStepsProps = {
	steps: StatusStep[];
	isActive: boolean;
	size?: 'large' | 'small';
};

export function StatusSteps({steps, isActive, size = 'large'}: StatusStepsProps): JSX.Element {
	const {formatMessage} = useIntl();

	const [fadingIds, setFadingIds] = useState<ReadonlySet<string>>(new Set());
	const [removedIds, setRemovedIds] = useState<ReadonlySet<string>>(new Set());
	const scheduledIds = useRef<Set<string>>(new Set());
	const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

	// Schedule fade for each chip as soon as it transitions to done.
	// No cleanup return here — clearing timers on every steps change would cancel
	// fade timers that were already scheduled for earlier chips.
	// Cleanup happens only in the unmount effect below.
	useEffect(() => {
		steps.forEach(step => {
			if (!step.done || scheduledIds.current.has(step.id)) return;
			scheduledIds.current.add(step.id);

			const fadeTimer = setTimeout(() => {
				setFadingIds(prev => new Set([...prev, step.id]));

				const removeTimer = setTimeout(() => {
					setRemovedIds(prev => new Set([...prev, step.id]));
				}, FADE_DURATION_MS);

				timersRef.current.push(removeTimer);
			}, FADE_DELAY_MS);

			timersRef.current.push(fadeTimer);
		});
	}, [steps]);

	// Clear all timers on unmount only
	useEffect(() => {
		return () => timersRef.current.forEach(clearTimeout);
	}, []);

	// Reset everything when a new stream starts
	useEffect(() => {
		if (isActive) {
			timersRef.current.forEach(clearTimeout);
			timersRef.current = [];
			scheduledIds.current = new Set();
			setFadingIds(new Set());
			setRemovedIds(new Set());
		}
	}, [isActive]);

	function resolveLabel(step: StatusStep): string {
		if (step.stepType === 'thinking') return formatMessage({id: 'forecast_agent.step.thinking'});
		if (step.stepType === 'collating') return formatMessage({id: 'forecast_agent.step.collating'});
		const toolName = step.toolName ?? '';
		const key = TOOL_KEY_MAP[toolName];
		if (key) return formatMessage({id: key});
		return formatMessage({id: 'forecast_agent.step.tool.unknown'}, {toolName});
	}

	const visibleSteps = steps.filter(s => !removedIds.has(s.id));
	const firstPendingVisible = visibleSteps.findIndex(s => !s.done);

	if (visibleSteps.length === 0) return <></>;

	return (
		<StepsWrapper>
			<StepsRow>
				{visibleSteps.map((step, i) => {
					const isCurrent = isActive && i === firstPendingVisible;
					const state = step.done ? 'done' : isCurrent ? 'active' : 'idle';
					return (
						<StepChip key={step.id} state={state} size={size} fading={fadingIds.has(step.id)}>
							{size === 'large' ? (
								<>
									{step.done ? <CheckIcon /> : isCurrent ? <SpinnerIcon /> : <IdleIcon />}
									{resolveLabel(step)}
								</>
							) : (
								<ChipInner>
									<ChipDot state={state} />
									{resolveLabel(step)}
								</ChipInner>
							)}
						</StepChip>
					);
				})}
			</StepsRow>
		</StepsWrapper>
	);
}
